import { useEffect, useState } from 'react'

import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { ptBR } from 'date-fns/locale'
import { Card, Col, Container, Row } from 'react-bootstrap'
import { Bar, Doughnut } from 'react-chartjs-2'
import Jet from '../../assets/images/Jetski.svg'
import Lancha from '../../assets/images/Lancha.svg'
import Loader from '../../components/loader/loader'
import OperationHeader from '../headers/operation.header'
import styles from './HourlyPrediction.module.scss'
import lclService from '../../services/lcl.service'
import authService from '../../services/auth.service'
import {
  dateAdd,
  dateToString,
  toDate,
  toInt
} from '../../utilities/auxiliary-functions'
import { removeFormatDate } from '../../utilities/masks'
import { isEmpty } from '../../utilities/validators'
import { VESSEL_JETSKI } from '../../utilities/constants'
import { usePartnerContext } from '../../contexts/partner.context'
import DashboardHeader from '../headers/dashboard.header'
import { Box } from '@mui/material'
import { StripBox, StripCard } from './components/StripBox'

interface IHourlyPredictionProps {}

export const HourlyPrediction: React.FC<IHourlyPredictionProps> = () => {

    let { partner } = usePartnerContext();

    let reloadPage: boolean = false;
    const [ loading, setLoading ] = useState(false);
    
    const [dateFilter, setDateFilter] = useState(new Date())

    const [rushDate, setRushDate] = useState('')
    const [rushHour, setRushHour] = useState('')
    const [expectedPeople, setExpectedPeople] = useState('')
    const [dearPeople, setDearPeople] = useState('')
    const [vessels, setVessels] = useState([])
    const [checkIns, setCheckIns] = useState([])
    const [forecasts, setForecasts] = useState([])

    ChartJS.register(
        CategoryScale,
        LinearScale,
        BarElement,
        ChartDataLabels,
        Title,
        Tooltip,
        Legend,
        ArcElement
    )

    const barOptions = {
        responsive: true,
        gridLines: {
            display: false
        },
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: {
                    color: '#FFFFFF',
                    font: {
                        size: 12,
                        family: "'Montserrat', sans-serif"
                    }
                }
            },
            datalabels: {
                color: 'white',
                font: {
                    size: 12,
                    family: "'Montserrat', sans-serif"
                }
            }
        },
        maintainAspectRatio: true,
        scales: {
            x: {
                ticks: {
                    color: 'white',
                    font: {
                        size: 12,
                        family: "'Montserrat', sans-serif"
                    }
                },
                grid: {
                    display: true,
                    tickColor: 'white',
                    drawOnChartArea: false,
                    color: 'white'
                },
                border: {
                    color: 'white'
                }
            },
            y: {
                ticks: {
                    color: 'white',
                    font: {
                        size: 12,
                        family: "'Montserrat', sans-serif"
                    }
                },
                grid: {
                    display: true,
                    tickColor: 'white',
                    drawOnChartArea: false,
                    color: 'white'
                },
                border: {
                    color: 'white'
                }
            }
        }
    }

    const predictedDailyData = {
        labels: checkIns.map((checkIn: any) => {
            return dateToString(checkIn.dateMovement, 'dd/mm/yyyy')
        }),
        datasets: [
            {
                label: 'Pendentes',
                data: checkIns.map((checkIn: any) => {
                    return checkIn.pending
                }),
                backgroundColor: '#3232D6'
            },{
                label: 'Confirmados',
                data: checkIns.map((checkIn: any) => {
                    return checkIn.confirmed
                }),
                backgroundColor: '#87B972',
                datalabels: {
                    display: false
                }
            },{
                label: 'Finalizados',
                data: checkIns.map((checkIn: any) => {
                    return checkIn.finished
                }),
                backgroundColor: '#FFB034',
                datalabels: {
                    display: false
                }
            },{
                label: 'Cancelados',
                data: checkIns.map((checkIn: any) => {
                    return checkIn.canceled
                }),
                backgroundColor: '#CF4147',
                datalabels: {
                    display: false
                }
            }
        ]
    }

    const predictedHourlyData = {
        labels: forecasts.map((forecast: any) => forecast.departure),
        datasets: [
            {
                label: 'Previstos',
                data: forecasts.map((forecast: any) => toInt(forecast.planned)),
                backgroundColor: '#3232D6',
                barThickness: 35
            },{
                label: 'Realizados',
                data: forecasts.map((forecast: any) => toInt(forecast.performed)),
                backgroundColor: '#87B972',
                datalabels: {
                    display: false
                },
                barThickness: 35
            }
        ]
    }

    const dataType = {
        labels: vessels.map((vessel: any) =>
            toInt(vessel.typeVessel) === VESSEL_JETSKI ? 'Jet-Ski' : 'Lancha'
        ),
        datasets: [
            {
                data: vessels.map((vessel: any) => {
                    var some = 0;
                    vessels.forEach((v: any) => {
                        some += v.qty;
                    })
                    return some > 0 ? Math.round((vessel.qty / some) * 100) : 0;
                }),
                backgroundColor: ['#00007A', '#91A9AF'],
                borderWidth: 0
            }
        ]
    }

    const typeOptions = {
        responsive: true,
        cutout: 60,
        gridLines: {
            display: false
        },
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: {
                    color: '#FFFFFF',
                    font: {
                        size: 14,
                        family: "'Montserrat', sans-serif"
                    }
                }
            },
            datalabels: {
                color: 'white',
                font: {
                    size: 14,
                    family: "'Montserrat', sans-serif"
                },
                formatter(value: any) {
                    return `${value}%`
                }
            }
        }
    }

    async function searchFilter(dateSearch: Date | null) {
        if (dateSearch === null) dateSearch = new Date()

        setDateFilter(dateSearch);
        const partner = authService.currentPartner();
        const view = await lclService.hourlyPrediction(partner.id, removeFormatDate(dateToString(dateSearch, 'dd/mm/yyyy')), 7);

        setRushDate(view.rushDate);
        setRushHour(view.rushHour);
        setExpectedPeople(view.expectedPeople);
        setDearPeople(view.dearPeople);
        setVessels(view.vessels);

        const localCheckIn = [] as any;
        for (var day = 0; day < 7; day++) {
            const dateNow = new Date(dateSearch);
            const dateS = removeFormatDate(
                dateToString(dateAdd('d', dateNow, day), 'dd/mm/yyyy')
            )
            const filter = view.checkIns.filter(
                (checkIn: any) => checkIn.dateMovement === dateS
            )
            if (filter.length > 0) 
                localCheckIn[localCheckIn.length] = filter[0]
            else
                localCheckIn[localCheckIn.length] = {
                    dateMovement: dateS,
                    canceled: '0',
                    confirmed: '0',
                    finished: '0',
                    pending: '0',
                    qty: 0
                }
        }
        setCheckIns(localCheckIn)

        const localForecast = [] as any
        const hourStart = dateAdd('H', new Date(dateSearch), 8)
        for (var hour = 0; hour < 11; hour++) {
            const hourNow = dateToString(
                dateAdd('H', new Date(hourStart), hour),
                'HH:00'
            )
            const filter = view.forecasts.filter(
                (forecast: any) => forecast.departure === hourNow
            )
            if (filter.length > 0) 
                localForecast[localForecast.length] = filter[0]
            else
                localForecast[localForecast.length] = {
                    departure: hourNow,
                    performed: '0',
                    planned: '0',
                    qty: 0
                }
        }
        setForecasts(localForecast)
    }

    useEffect(() => {
        if (isEmpty(partner))
            // eslint-disable-next-line react-hooks/exhaustive-deps
            partner = authService.currentPartner();

        if (!reloadPage) {
            reloadPage = true;

            setLoading(true)
            setTimeout(() => {
                searchFilter(new Date()).finally(() => {
                    setLoading(false);
                });
            }, 500);
        }
    }, []);

    function viewDashboard() {
        return (
            <Container fluid className="page-body dashboards">
                {/*}
                <Col md={12} className="mb-4 d-flex justify-content-between">
                    <div className="me-2"></div>
                    <div className="ms-3 d-flex">
                    <div
                        className="d-flex flex-column align-items-center me-5"
                        style={{ width: 250 }}
                    >
                        <h5>Data Pico</h5>
                        <h2 className="lighter">
                        {isEmpty(rushDate)
                            ? ''
                            : dateToString(
                                toDate(rushDate, 'yyyy-mm-ddTHH:MM:ss.sssZ'),
                                'dd/mm/yyyy'
                            )}
                        </h2>
                    </div>
                    <div
                        className="d-flex flex-column align-items-center me-5"
                        style={{ width: 250 }}
                    >
                        <h5>Horário Pico</h5>
                        <h2 className="lighter">{rushHour}</h2>
                    </div>
                    <div
                        className="d-flex flex-column align-items-center me-5"
                        style={{ width: 250 }}
                    >
                        <h5>Pessoas Previstas</h5>
                        <h2 className="lighter">{expectedPeople}</h2>
                    </div>
                    <div
                        className="d-flex flex-column align-items-center"
                        style={{ width: 250 }}
                    >
                        <h5>Pessoas Estimadas</h5>
                        <h2 className="lighter">{dearPeople}</h2>
                    </div>
                    </div>
                    <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns} >
                        <DesktopDatePicker
                            label="Previsão Seguinte"
                            sx={{ width: 130, fontSize: 42 }}
                            slotProps={{
                            textField: {
                                variant: 'standard',
                                helperText: 'Exibindo 7 dias'
                            }
                            }}
                            value={dateFilter}
                            onChange={date => searchFilter(date)}
                            className="mt-2 me-3"
                            // minDate={new Date()}
                        />
                    </LocalizationProvider>
                </Col>
                */}
                <Box className="dashboards-search">
                    <LocalizationProvider
                        adapterLocale={ptBR}
                        dateAdapter={AdapterDateFns}
                    >
                        <DesktopDatePicker
                            label="Buscar período"
                            sx={{ width: 160, fontSize: 42 }}
                            slotProps={{
                                textField: {
                                variant: 'standard',
                                helperText: 'Exibindo 7 dias',
                                }
                            }}
                            value={dateFilter} 
                            onChange={(date) => searchFilter(date)}
                            className="mt-2 me-3"
                            // minDate={new Date()}
                        />
                    </LocalizationProvider>
                </Box>
                <StripBox>
                    <StripCard className="blue" label="Data Pico" value={isEmpty(rushDate) ? '' : dateToString(toDate(rushDate, 'yyyy-mm-ddTHH:MM:ss.sssZ'), 'dd/mm/yyyy')} />
                    <StripCard className="blue" label="Horário Pico" value={rushHour} />
                    <StripCard className="blue" label="Pessoas Previstas" value={expectedPeople} />
                    <StripCard className="blue" label="Pessoas Estimadas" value={dearPeople} />
                </StripBox>
                <Box className="d-flex flex-column gap-2">
                    <Box className="d-flex justify-content-between gap-2">
                        <Card>
                            <Card.Header>
                                <Card.Title>Embarcações</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Doughnut data={dataType} options={typeOptions} />
                            </Card.Body>
                        </Card>
                        <Card className='flex-grow-1'>
                            <Card.Header>
                                <Card.Title>Checkin-in</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Bar options={barOptions} data={predictedDailyData} height={96} />
                            </Card.Body>
                        </Card>
                    </Box>
                    <Box className="d-flex justify-content-between gap-2">
                        <Card className='flex-grow-1'>
                            <Card.Header>
                                <Card.Title>Previsão Horária</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Bar options={barOptions} data={predictedHourlyData} height={48} />
                            </Card.Body>
                        </Card>
                    </Box>
                </Box>
            </Container>
        )
    }
    
    return (
        <div className="dashboard">
            <DashboardHeader title="Previsão" description="" />
            {loading ? <Loader /> : viewDashboard()}
        </div>
    );

}

export default HourlyPrediction

